/**
 * TemplateGrid — Grid of agent template cards for selection in the builder.
 */
import { Bot } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/lib/cn";
import type { AgentTemplate } from "@/types/agent";

interface TemplateGridProps {
  templates: AgentTemplate[];
  selectedId: string | null;
  onSelect: (template: AgentTemplate) => void;
}

export function TemplateGrid({ templates, selectedId, onSelect }: TemplateGridProps) {
  if (templates.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No templates available
      </p>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="template-grid">
      {templates.map((tpl) => (
        <button
          key={tpl.id}
          type="button"
          onClick={() => onSelect(tpl)}
          className={cn(
            "flex flex-col items-start rounded-xl border p-4 text-left transition-colors",
            selectedId === tpl.id
              ? "border-primary bg-primary/5"
              : "border-white/10 bg-surface-raised hover:border-white/20",
          )}
          data-testid={`template-card-${tpl.id}`}
        >
          <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Bot className="h-5 w-5" />
          </div>
          <h3 className="text-sm font-semibold text-white">{tpl.name}</h3>
          <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
            {tpl.description}
          </p>
          {tpl.defaultConfig?.model && (
            <Badge variant="primary" className="mt-2 text-[10px]">
              {tpl.defaultConfig.model}
            </Badge>
          )}
        </button>
      ))}
    </div>
  );
}
